import React from 'react';
import dayjs from 'dayjs';
import { getStatusCategoriaTorneioDisplay } from '../services/categoriaTorneioService';
import { formatModeloDisputa } from '../utils/categoriaTorneioLabels';

interface CategoriaInfo {
  status?: string | null;
  genero?: string | null;
  numeroAtletas: number;
  vagas?: number | null;
  dataInicio?: string | null;
  dataFim?: string | null;
  dataLimPag?: string | null;
  modeloDisputa?: string | null;
  numeroSets?: number | null;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
  precoInscricao: number;
  idadeInicial?: number | null;
  idadeFinal?: number | null;
  idadeCombinada?: number | null;
  descricao?: string | null;
  torneio?: { dataInscricaoInicio?: string | null; dataInscricaoFim?: string | null } | null;
}

interface CategorySobreTabProps {
  categoria: CategoriaInfo;
  vagasOcupadas?: number | null;
  exibirInscritos?: boolean | null;
}

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY') : 'Não definido') : 'Não definido';

// formatModeloDisputa vem do util central
// (src/utils/categoriaTorneioLabels.ts) — fonte única dos rótulos, evitando manter
// dois mapas em sincronia manual ao adicionar novos modelos (ex.: REI_RAINHA/SUPER_8).

const getGeneroLabel = (genero?: string | null): string => {
  switch (genero) {
    case 'MASCULINO': return 'Masculino';
    case 'FEMININO': return 'Feminino';
    case 'MISTO': return 'Misto';
    default: return 'Não definido';
  }
};

const getTipoLabel = (numeroAtletas?: number): string => {
  if (numeroAtletas === 1) return 'Simples';
  if (numeroAtletas === 2) return 'Duplas';
  return 'Não definido';
};

const formatPreco = (preco?: number | null): string => {
  if (preco == null || preco === 0) return 'Grátis';
  return preco.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const CategorySobreTab: React.FC<CategorySobreTabProps> = ({ categoria, vagasOcupadas, exibirInscritos }) => {
  return (
    <div>
      {/* Informações Gerais */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-4">
          <h4 className="fw-700 mb-3">Informações Gerais</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Status:</strong>
              <p className="mb-0">
                {(() => {
                  const statusDisplay = getStatusCategoriaTorneioDisplay(categoria.status);
                  return <span className={`badge ${statusDisplay.color}`}>{statusDisplay.label}</span>;
                })()}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Gênero:</strong>
              <p className="text-muted mb-0">{getGeneroLabel(categoria.genero)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Tipo:</strong>
              <p className="text-muted mb-0">{getTipoLabel(categoria.numeroAtletas)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Vagas:</strong>
              <p className="text-muted mb-0">
                {categoria.vagas != null
                  ? (vagasOcupadas != null && exibirInscritos
                    ? `${vagasOcupadas}/${categoria.vagas}`
                    : `${categoria.vagas}`)
                  : 'Não definido'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Datas */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-4">
          <h4 className="fw-700 mb-3">Datas</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Período da Categoria:</strong>
              <p className="text-muted mb-0">
                {categoria.dataInicio || categoria.dataFim
                  ? `${formatDate(categoria.dataInicio)} até ${formatDate(categoria.dataFim)}`
                  : 'Não definido'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Período de Inscrições:</strong>
              <p className="text-muted mb-0">
                {categoria.torneio?.dataInscricaoInicio || categoria.torneio?.dataInscricaoFim
                  ? `${formatDate(categoria.torneio?.dataInscricaoInicio)} até ${formatDate(categoria.torneio?.dataInscricaoFim)}`
                  : 'Não definido'}
              </p>
            </div>
            {categoria.dataLimPag && (
              <div className="col-md-6 mb-3">
                <strong>Limite de Pagamento:</strong>
                <p className="text-muted mb-0">{formatDate(categoria.dataLimPag)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Formato de Competição */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-4">
          <h4 className="fw-700 mb-3">Formato de Competição</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Modelo de Disputa:</strong>
              <p className="text-muted mb-0">{formatModeloDisputa(categoria.modeloDisputa)}</p>
            </div>
            {categoria.numeroSets != null && (
              <div className="col-md-6 mb-3">
                <strong>Número de Sets:</strong>
                <p className="text-muted mb-0">{categoria.numeroSets}</p>
              </div>
            )}
            {categoria.gamesPorSets != null && (
              <div className="col-md-6 mb-3">
                <strong>Games por Set:</strong>
                <p className="text-muted mb-0">{categoria.gamesPorSets}</p>
              </div>
            )}
            {categoria.tiebreaks != null && (
              <div className="col-md-6 mb-3">
                <strong>Tiebreak:</strong>
                <p className="text-muted mb-0">{categoria.tiebreaks}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inscrição */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-4">
          <h4 className="fw-700 mb-3">Inscrição</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Preço:</strong>
              <p className="text-muted mb-0">{formatPreco(categoria.precoInscricao)}</p>
            </div>
            {(categoria.idadeInicial != null || categoria.idadeFinal != null) && (
              <div className="col-md-6 mb-3">
                <strong>Faixa Etária:</strong>
                <p className="text-muted mb-0">
                  {categoria.idadeInicial != null && categoria.idadeFinal != null
                    ? `${categoria.idadeInicial} a ${categoria.idadeFinal} anos`
                    : categoria.idadeInicial != null
                      ? `A partir de ${categoria.idadeInicial} anos`
                      : `Até ${categoria.idadeFinal} anos`}
                </p>
              </div>
            )}
            {categoria.idadeCombinada != null && (
              <div className="col-md-6 mb-3">
                <strong>Idade Combinada:</strong>
                <p className="text-muted mb-0">{categoria.idadeCombinada} anos</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Descrição */}
      {categoria.descricao && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body p-4">
            <h4 className="fw-700 mb-3">Descrição</h4>
            <p className="text-muted mb-2">{categoria.descricao}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategorySobreTab;
